/** "What do I do next?" — the overview's worklist.
 *
 *  A ranked list rather than a grid of cards, on purpose: position
 *  carries priority, and equal-sized cards would say a 7 and a 489
 *  matter equally. The magnitude bar then shows which problem is the
 *  size of the whole campaign.
 *
 *  The empty state is a feature. A campaign with nothing wrong must read
 *  as reassurance, not as a list that failed to load.
 */
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { campaignActions, type ActionInput, type ActionSeverity } from "./campaignActions";

const TONE: Record<ActionSeverity, { rule: string; tile: string; num: string }> = {
  critical: {
    rule: "bg-cvp-danger",
    tile: "border-cvp-danger/40 bg-cvp-danger/10",
    num: "text-red-300",
  },
  warning: {
    rule: "bg-nb-warn",
    tile: "border-nb-warn/40 bg-nb-warn/10",
    num: "text-amber-300",
  },
  note: { rule: "bg-nb-text-3", tile: "border-nb-border bg-nb-bg", num: "" },
};

const ICON: Record<string, string> = {
  overdue: "◷",
  nonConformRate: "⚑",
  neverInvited: "✉",
  noShow: "⊘",
  unjudged: "◇",
};

export function CampaignActionQueue(input: ActionInput) {
  const { t } = useTranslation();
  const items = campaignActions(input);

  if (items.length === 0) {
    return (
      <section className="card">
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-nb-text-2">
          {t("campaigns.actions.title")}
        </h2>
        <p className="flex items-center gap-2 rounded-lg border border-nb-mint/25 bg-nb-mint/5 px-3 py-2.5 text-sm text-nb-text-2">
          <span aria-hidden className="text-nb-mint">
            ✓
          </span>
          {t("campaigns.actions.allClear")}
        </p>
      </section>
    );
  }

  return (
    <section className="card">
      <header className="mb-2.5 flex items-baseline gap-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
          {t("campaigns.actions.title")}
        </h2>
        <span className="ml-auto rounded-full border border-nb-border px-2 py-0.5 font-mono text-[10px] text-nb-text-3">
          {t("campaigns.actions.count", { count: items.length })}
        </span>
      </header>

      <ul className="flex flex-col gap-1.5">
        {items.map((item) => {
          const tone = TONE[item.severity];
          return (
            <li
              key={item.key}
              className="relative grid grid-cols-[2.1rem_1fr_auto] items-center gap-3 overflow-hidden rounded-lg border border-nb-border bg-nb-surface px-3 py-2.5"
            >
              <span
                aria-hidden="true"
                className={`absolute inset-y-0 left-0 w-[3px] ${tone.rule}`}
              />
              <span
                aria-hidden="true"
                className={`flex h-8 w-8 items-center justify-center rounded-lg border text-sm ${tone.tile}`}
              >
                {ICON[item.key] ?? "•"}
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-medium">
                  <span className={`font-mono font-bold ${tone.num}`}>
                    {item.key === "nonConformRate"
                      ? `${item.count}%`
                      : item.count.toLocaleString()}
                  </span>{" "}
                  {t(`campaigns.actions.title_${item.key}`)}
                </span>
                <span className="mt-0.5 block text-xs text-nb-text-3">
                  {t(`campaigns.actions.sub_${item.key}`, {
                    count: item.count,
                  })}
                </span>
                <span className="mt-1.5 block h-[3px] max-w-[18rem] overflow-hidden rounded-full bg-nb-bg">
                  <span
                    className={`block h-full rounded-full ${tone.rule}`}
                    style={{ width: `${Math.max(2, item.magnitude)}%` }}
                  />
                </span>
              </span>
              {item.to && (
                <Link
                  to={item.to}
                  className="whitespace-nowrap rounded-md border border-nb-mint/25 bg-nb-mint/5 px-2.5 py-1.5 text-xs font-medium text-nb-mint"
                >
                  {t(`campaigns.actions.cta_${item.key}`)}
                </Link>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
